import { DIMENSION_IDS } from '@/constants'
import { useCountry } from '@/composables/useParams/params/country'
import { useDimension } from '@/composables/useParams/params/dimension'

/**
 * Use the data framework structure.
 */
export const useFramework = createSharedComposable(() => {
    /**
     * Use metadata.
     */
    const { getDimensionsForView, dimensions: metaDimensions } = useMeta()

    /**
     * Current dimension ID.
     */
    const dimension = useDimension()

    /**
     * Current view mode (country or world).
     */
    const [country] = useCountry()

    /**
     * The framework structure.
     */
    const framework = ref<Dimension[]>([])

    /**
     * Custom framework for the world view.
     */
    const worldFramework = computed(() => {
        return unref(framework).concat(getDimensionsForView('world'))
    })

    /**
     * Custom framework for the country view.
     */
    const countryFramework = computed(() => {
        return unref(framework).concat(getDimensionsForView('country'))
    })

    /**
     * Current framework to use.
     */
    const currentFramework = computed(() => {
        return unref(country) ? unref(countryFramework) : unref(worldFramework)
    })

    /**
     * Entire framework.
     */
    const entireFramework = computed(() => {
        return unref(framework).concat(unref(metaDimensions))
    })

    /**
     * State of fetched items.
     */
    const fetched = reactive({
        parents: computed(() => {
            return new Set(unref(framework).map(({ parent }) => parent))
        }),
        items: computed(() => {
            return new Set(unref(framework).map(({ id }) => id))
        })
    })

    /**
     * Fetch children of the given dimension.
     */
    async function fetchChildren(parent?: Dimension['id']) {
        if (!parent) return fetchRoot()

        if (!fetched.items.has(parent) && !isMeta(parent)) {
            await fetchPath(parent)
        }

        if (isMeta(parent) || !hasChildren(parent)) return

        if (!fetched.parents.has(parent)) {
            await redirectToRootIfError(async () => {
                const data = await apiFetch<Dimension[]>(
                    `dimensions/${parent}/children`
                )
                add(...data)
            })
        }

        return findChildren(parent)
    }

    /**
     * Fetch all the children of the given dimension.
     */
    async function fetchAllChildren(parent?: Dimension['id']) {
        if (!parent) return fetchRoot()

        if (isMeta(parent) || !hasChildren(parent)) return

        return await redirectToRootIfError(async () => {
            const data = await apiFetch<Dimension[]>(
                `dimensions/${parent}/all-children`
            )
            add(...data)
            return data
        })
    }

    /**
     * Fetch the root levels.
     */
    async function fetchRoot() {
        const list = unref(framework)

        if (list.length) return list

        return await redirectToRootIfError(async () => {
            const data = await apiFetch<Dimension[]>('dimensions')
            add(...data)
            return data
        })
    }

    /**
     * Fetch all the dimensions including parents and siblings
     * down to this level.
     */
    async function fetchPath(id?: Dimension['id']) {
        if (!id) return fetchRoot()

        if (!fetched.items.has(id) && !isMeta(id)) {
            await redirectToRootIfError(async () => {
                const data = await apiFetch<Dimension[]>(
                    `dimensions/${id}/path`
                )
                add(...data)
            })
        }

        return find(id)
    }

    /**
     * Redirect to the root view if an error occured.
     */
    async function redirectToRootIfError<T>(cb: () => Promise<T>) {
        try {
            return await cb()
        } catch (e) {
            console.warn(e)
            dimension.value = undefined
        }
    }

    /**
     * This is the initial fetch. It will attempt
     * to fetch the data unless it is a "meta" dimension.
     */
    async function fetch() {
        const id = unref(dimension)

        await fetchRoot()

        if (isMeta(id)) return

        await fetchPath(id)
        await fetchChildren(id)
    }

    /**
     * Add dimensions to the framework.
     */
    function add(...dimensions: Dimension[]) {
        const { items } = fetched
        const toAdd = dimensions.filter(dimension => {
            return !items.has(dimension.id) && items.add(dimension.id)
        })

        unref(framework).push(...toAdd.sort(sortByID))
    }

    /**
     * Whether the current ID a meta dimension.
     */
    function isMeta(id?: Dimension['id']) {
        if (!id) return false

        return unref(metaDimensions).some(({ id: metaId }) => metaId === id)
    }

    /**
     * Find a dimension.
     */
    function find(id?: Dimension['id']) {
        return unref(currentFramework).find(dimension => dimension.id === id)
    }

    /**
     * Whether a dimension has children.
     */
    function hasChildren(id?: Dimension['id']) {
        return find(id)?.hasChildren !== false
    }

    /**
     * Find a dimension's children.
     */
    function findChildren(id?: Dimension['id']) {
        return unref(currentFramework).filter(
            dimension => dimension.parent === id
        )
    }

    /**
     * Check if a dimension is of the given type.
     */
    function isDimension(
        test: Dimension['id'],
        value?: Dimension | Dimension['id']
    ) {
        if (typeof value === 'undefined') return

        return typeof value === 'object' ? value.id === test : value === test
    }

    /**
     * Check if a dimension is "government response".
     */
    function isGovernmentResponse(dim?: Dimension | Dimension['id']) {
        return isDimension(DIMENSION_IDS.GOV_RESPONSE, dim)
    }

    /**
     * Check if a dimension is "vulnerability".
     */
    function isVulnerability(dim?: Dimension | Dimension['id']) {
        return isDimension(DIMENSION_IDS.VULNERABILITY, dim)
    }

    /**
     * Check if a dimension is "prevalence".
     */
    function isPrevalence(dim?: Dimension | Dimension['id']) {
        return isDimension(DIMENSION_IDS.PREVALENCE, dim)
    }

    /**
     * Check if a dimension is "GDP correlation".
     */
    function isGdpCorrelation(dim?: Dimension | Dimension['id']) {
        return isDimension(DIMENSION_IDS.GDP_CORRELATION, dim)
    }

    /**
     * Check if a dimension is "importing risk".
     */
    function isImportingRisk(dim?: Dimension | Dimension['id']) {
        return isDimension(DIMENSION_IDS.IMPORTING_RISK, dim)
    }

    /**
     * Only top level dimensions.
     */
    function onlyRoots(dimensions: Dimension[]) {
        return dimensions.filter(({ parent }) => !parent)
    }

    /**
     * Desired order of the dimensions.
     */
    const orderOfDimensions: string[] = [
        DIMENSION_IDS.PREVALENCE,
        DIMENSION_IDS.VULNERABILITY,
        DIMENSION_IDS.GOV_RESPONSE,
        DIMENSION_IDS.GDP_CORRELATION,
        DIMENSION_IDS.IMPORTING_RISK
    ]

    /**
     * Sort the dimensions.
     */
    function sortByID(a: Dimension, b: Dimension) {
        return orderOfDimensions.indexOf(a.id) - orderOfDimensions.indexOf(b.id)
    }

    /**
     * Watch the current dimension and fetch its children.
     */
    watch(dimension, dimension => fetchChildren(dimension))

    return {
        fetch,
        fetchRoot,
        fetchChildren,
        fetchPath,
        fetchAllChildren,
        find,
        dimension,
        findChildren,
        framework,
        worldFramework,
        countryFramework,
        isDimension,
        isGovernmentResponse,
        isVulnerability,
        isPrevalence,
        isGdpCorrelation,
        isImportingRisk,
        entireFramework,
        onlyRoots
    }
})
